import React, { useState, useEffect } from 'react';
import { Calendar, Users2, Tag, UserCircle, Users, Archive, CheckCircle2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useFollowUps } from '../context/FollowUpsContext';
import { supabase } from '../utils/supabase';

interface FollowUp {
  id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'in_progress' | 'completed';
  assignee: string | null;
  due_date: string | null;
  tag: string;
  summary: {
    id: string;
    title: string;
    date: string;
    double: {
      name: string;
    };
  };
}

const tagConfig = {
  triage: { label: 'Triage', color: 'bg-blue-500' },
  own: { label: 'Own', color: 'bg-green-500' },
  delegate: { label: 'Delegate', color: 'bg-yellow-500' },
  archive: { label: 'Archive', color: 'bg-red-500' }
};

const FollowUps = () => {
  const [searchParams] = useSearchParams();
  const summaryId = searchParams.get('summary');
  const { followUps, updateFollowUp } = useFollowUps();
  const [followUpItems, setFollowUpItems] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animatingCards, setAnimatingCards] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchFollowUps = async () => {
      try {
        const query = supabase
          .from('follow_ups')
          .select(`
            *,
            summary:summary_id (
              id,
              title,
              date,
              double:double_id (name)
            )
          `)
          .order('created_at', { ascending: false });

        if (summaryId) {
          query.eq('summary_id', summaryId);
        }

        const { data, error: fetchError } = await query;
        
        if (fetchError) throw fetchError;
        setFollowUpItems(data || []);
      } catch (err) {
        console.error('Error fetching follow-ups:', err);
        setError('Failed to load follow-ups');
      } finally {
        setLoading(false);
      }
    };

    fetchFollowUps();
  }, [summaryId]);

  const handleCheckboxChange = async (followUpId: string, checked: boolean) => {
    try {
      setAnimatingCards(prev => ({ ...prev, [followUpId]: true }));

      const { error: updateError } = await supabase
        .from('follow_ups')
        .update({ 
          status: checked ? 'completed' : 'pending',
          tag: checked ? 'archive' : 'triage'
        })
        .eq('id', followUpId);

      if (updateError) throw updateError;

      // Update local state
      setFollowUpItems(prev => 
        prev.map(item => 
          item.id === followUpId 
            ? { 
                ...item, 
                status: checked ? 'completed' : 'pending',
                tag: checked ? 'archive' : 'triage'
              } 
            : item
        )
      );

      setTimeout(() => {
        setAnimatingCards(prev => ({ ...prev, [followUpId]: false }));
      }, 300);
    } catch (err) {
      console.error('Error updating follow-up:', err);
      setError('Failed to update follow-up status');
    }
  };

  const handleTagChange = async (followUpId: string, newTag: 'triage' | 'own' | 'delegate' | 'archive') => {
    try {
      setAnimatingCards(prev => ({ ...prev, [followUpId]: true }));

      const { error: updateError } = await supabase
        .from('follow_ups')
        .update({ tag: newTag })
        .eq('id', followUpId);

      if (updateError) throw updateError;

      // Update local state
      setFollowUpItems(prev => 
        prev.map(item => 
          item.id === followUpId 
            ? { ...item, tag: newTag }
            : item
        )
      );

      setTimeout(() => {
        setAnimatingCards(prev => ({ ...prev, [followUpId]: false }));
      }, 300);
    } catch (err) {
      console.error('Error updating follow-up tag:', err);
      setError('Failed to update follow-up tag');
    }
  };

  const handleDelegateClick = (followUp: FollowUp) => {
    const subject = encodeURIComponent(followUp.title);
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to delegate this task to you:\n\n` +
      `Task: ${followUp.title}\n` +
      `Description: ${followUp.description}\n\n` +
      `Context from meeting "${followUp.summary.title}":\n` +
      `Due date: ${followUp.due_date || 'Not specified'}\n\n` +
      `Best regards`
    );
    
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-gray-600">Loading follow-ups...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        {error}
      </div>
    );
  }

  // Group follow-ups by tag
  const groupedFollowUps = followUpItems.reduce((acc, followUp) => {
    const tag = followUp.tag || 'triage';
    if (!acc[tag]) acc[tag] = [];
    acc[tag].push(followUp);
    return acc;
  }, {} as Record<string, FollowUp[]>);

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Follow-Ups</h1>
        <p className="text-gray-600">Track and manage action items from your meetings.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 auto-rows-min">
        {Object.entries(tagConfig).map(([tag, config]) => {
          const items = groupedFollowUps[tag] || [];
          const shouldShow = tag === 'triage' || items.length > 0;
          
          return shouldShow && (
            <section key={tag} className="space-y-4">
              <div className={`flex items-center gap-2 px-4 py-2 ${config.color} text-white rounded-lg`}>
                <Tag className="h-4 w-4" />
                <h2 className="font-semibold">{config.label}</h2>
                <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-sm">
                  {items.length}
                </span>
              </div>
              
              <div className="space-y-4 min-h-[100px]">
                {items.map((followUp) => (
                  <div
                    key={followUp.id}
                    className={`bg-white p-4 rounded-lg border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all overflow-hidden ${
                      animatingCards[followUp.id] ? 'follow-up-exit' : 'follow-up-enter'
                    }`}
                  >
                    <div className="flex items-start gap-3 w-full">
                      {(tag === 'own' || tag === 'delegate') && (
                        <input
                          type="checkbox"
                          checked={followUp.status === 'completed'}
                          onChange={(e) => handleCheckboxChange(followUp.id, e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-2 border-gray-300 text-primary focus:ring-primary"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 mb-2 truncate">{followUp.title}</h3>
                          {tag === 'delegate' && (
                            <button
                              onClick={() => handleDelegateClick(followUp)}
                              className="text-sm font-medium text-yellow-600 hover:text-yellow-700 group flex-shrink-0 ml-2"
                            >
                              <span className="group-hover:hidden">Ready to delegate</span>
                              <span className="hidden group-hover:inline">Click to delegate</span>
                            </button>
                          )}
                          {tag === 'own' && (
                            <span className="text-sm font-medium text-green-600 flex-shrink-0 ml-2">Owned</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 break-words">{followUp.description}</p>
                        
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <div className="flex items-center justify-between text-sm text-gray-600 flex-wrap gap-2">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>{followUp.summary.date}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Users2 className="h-4 w-4" />
                                <span>{followUp.assignee || 'Unassigned'}</span>
                              </div>
                              <a 
                                href={`/summaries?id=${followUp.summary.id}`}
                                className="text-primary hover:underline truncate max-w-[200px]"
                              >
                                {followUp.summary.title}
                              </a>
                            </div>
                            {tag === 'triage' && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => handleTagChange(followUp.id, 'own')}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                                  title="Own"
                                >
                                  <UserCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleTagChange(followUp.id, 'delegate')}
                                  className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-full transition-colors"
                                  title="Delegate"
                                >
                                  <Users className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleTagChange(followUp.id, 'archive')}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                  title="Archive"
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            {tag === 'own' && (
                              <button
                                onClick={() => handleTagChange(followUp.id, 'delegate')}
                                className="text-sm text-yellow-600 hover:text-yellow-700"
                              >
                                delegate instead?
                              </button>
                            )}
                            {tag === 'delegate' && (
                              <button
                                onClick={() => handleTagChange(followUp.id, 'own')}
                                className="text-sm text-green-600 hover:text-green-700"
                              >
                                own instead?
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default FollowUps;